import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Badge,
  Chip,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl
} from '@mui/material';
import { 
  DateRange as CalendarIcon, 
  People as PeopleIcon, 
  AssignmentTurnedIn as RequestsIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  TrendingUp as TrendingUpIcon,
  Timer as TimerIcon,
  WhatsApp as WhatsAppIcon,
  Message as MessageIcon,
  Campaign as CampaignIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../../services/api';
import { useSubscription } from '../../contexts/SubscriptionContext';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`instructor-tabpanel-${index}`}
      aria-labelledby={`instructor-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `instructor-tab-${index}`,
    'aria-controls': `instructor-tabpanel-${index}`,
  };
}

interface LessonRequest {
  id: number;
  studentName: string;
  lessonDate: string;
  duration: number;
  notes: string;
}

const InstructorDashboard: React.FC = () => {
  const [value, setValue] = useState(0);
  const [events, setEvents] = useState<Array<{ id: string; title: string; start: Date; end: Date }>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [lessonRequests, setLessonRequests] = useState<LessonRequest[]>([]);
  const [actionInProgress, setActionInProgress] = useState<number | null>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [stats, setStats] = useState({ totalStudents: 0, totalHours: 0, earnings: 0, pendingRequests: 0 });
  const [calendarView, setCalendarView] = useState<any>('week');
  const [calendarDate, setCalendarDate] = useState<Date>(new Date());

  // Custom Message Dialog State
  const [isMsgDialogOpen, setIsMsgDialogOpen] = useState(false);
  const [msgData, setMsgData] = useState({
    studentId: '',
    message: '',
    sendEmail: true,
    sendSms: true
  });
  const [isSendingMsg, setIsSendingMsg] = useState(false);

  const navigate = useNavigate();
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();

  useEffect(() => {
    if (!isSubscriptionLoading && !hasActiveSubscription) {
      navigate('/instructor/subscribe');
    } else if (!isSubscriptionLoading) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasActiveSubscription, isSubscriptionLoading, navigate]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [slotsResponse, requestsResponse, studentsResponse] = await Promise.all([
        api.getInstructorAvailabilitySlots(),
        api.getLessonRequests(),
        api.getStudents()
      ]);

      const slots = slotsResponse.data.map(slot => ({
        id: slot.id,
        title: slot.status === 'Pending' ? 'Pending Approval' : slot.status === 'Approved' ? 'Approved (Booked)' : slot.status === 'Completed' ? 'Completed' : 'Available',
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        status: slot.status || (slot.isBooked ? 'Approved' : 'Available')
      }));
      setEvents(slots);

      const requests = requestsResponse.data;
      setLessonRequests(requests);

      const studentsData = studentsResponse.data || [];
      setStudents(studentsData);

      // Calculate stats
      const totalStudents = studentsData.length;
      let totalMinutes = 0;
      studentsData.forEach(student => {
        if (student.lessons) {
          student.lessons.forEach((lesson: any) => {
            if (lesson.isCompleted) {
              const durMatch = lesson.description ? lesson.description.match(/\d+/) : null;
              const duration = durMatch ? parseInt(durMatch[0]) : 60;
              totalMinutes += duration;
            }
          });
        }
      });

      const totalHours = Math.round(totalMinutes / 60);
      const hourlyRate = 40; // £40 per hour in the UK
      const earnings = totalHours * hourlyRate;

      setStats({
        totalStudents,
        totalHours,
        earnings,
        pendingRequests: requests.length
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('An error occurred while loading data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailabilitySlots = async () => {
    try {
      const response = await api.getInstructorAvailabilitySlots();
      const slots = response.data.map(slot => ({
        id: slot.id,
        title: slot.status === 'Pending' ? 'Pending Approval' : slot.status === 'Approved' ? 'Approved (Booked)' : slot.status === 'Completed' ? 'Completed' : 'Available',
        start: new Date(slot.startTime),
        end: new Date(slot.endTime),
        status: slot.status || (slot.isBooked ? 'Approved' : 'Available')
      }));
      setEvents(slots);
    } catch (err) {
      console.error('Error fetching slots:', err);
    }
  };

  const fetchLessonRequests = async () => {
    try {
      const response = await api.getLessonRequests();
      setLessonRequests(response.data);
    } catch (err) {
      console.error('Error fetching lesson requests:', err);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleApproveRequest = async (id: number) => {
    try {
      setActionInProgress(id);
      setError(null);
      await api.approveLesson(id);
      setSuccess('Lesson request successfully approved.');
      setLessonRequests(prev => prev.filter(req => req.id !== id));
      await fetchAvailabilitySlots();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error approving request:', err);
      setError(err.response?.data?.message || 'An error occurred while approving the lesson.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectRequest = async (id: number) => {
    try {
      setActionInProgress(id);
      setError(null);
      await api.rejectLesson(id);
      setSuccess('Lesson request rejected.');
      setLessonRequests(prev => prev.filter(req => req.id !== id));
      await fetchAvailabilitySlots();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error rejecting request:', err);
      setError(err.response?.data?.message || 'An error occurred while rejecting the lesson.');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleOpenMessageDialog = (studentId: string = '') => {
    setMsgData({
      studentId,
      message: '',
      sendEmail: true,
      sendSms: true
    });
    setIsMsgDialogOpen(true);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!msgData.message.trim()) return;

    try {
      setIsSendingMsg(true);
      setError(null);
      await api.sendMessageToStudents({
        studentId: msgData.studentId !== '' ? msgData.studentId : undefined,
        message: msgData.message,
        sendEmail: msgData.sendEmail,
        sendSms: msgData.sendSms
      });
      setSuccess('Message/Announcement successfully sent.');
      setIsMsgDialogOpen(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.message || 'Failed to send message.');
    } finally {
      setIsSendingMsg(false);
    }
  };

  const eventStyleGetter = (event: any) => {
    let backgroundColor = '#1976d2'; // Default blue
    let border = 'none';

    if (event.status === 'Available') {
      backgroundColor = '#2e7d32'; // Green for available
    } else if (event.status === 'Pending') {
      backgroundColor = '#ed6c02'; // Orange/warning for pending
    } else if (event.status === 'Completed') {
      backgroundColor = '#757575'; // Grey for completed
    } else if (event.status === 'Approved') {
      backgroundColor = '#1565c0'; // Blue for approved/booked
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border,
        display: 'block',
        fontSize: '0.85rem',
        fontWeight: 'bold',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }
    };
  };

  if (isSubscriptionLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Instructor Dashboard
        </Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={() => navigate('/instructor/subscribe')}
            sx={{ mr: 2, borderRadius: 2 }}
          >
            Manage Subscription
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/instructor/availability/add')}
            sx={{ borderRadius: 2, fontWeight: 'bold' }}
          >
            Add Availability
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>
      )}

      {/* Stats Cards Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(25, 118, 210, 0.08)', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                <PeopleIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Total Students</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{stats.totalStudents}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(237, 108, 2, 0.08)', color: 'warning.main', display: 'flex', alignItems: 'center' }}>
                <TimerIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Lesson Hours</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>{stats.totalHours} Hours</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(46, 125, 50, 0.08)', color: 'success.main', display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Estimated Earnings</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>£{stats.earnings}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid #e0e0e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(211, 47, 47, 0.08)', color: 'error.main', display: 'flex', alignItems: 'center' }}>
                <RequestsIcon />
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>Pending Requests</Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>{stats.pendingRequests}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={value} 
            onChange={handleChange} 
            aria-label="instructor dashboard tabs"
            variant="fullWidth"
          >
            <Tab label="Calendar" icon={<CalendarIcon />} {...a11yProps(0)} />
            <Tab label="Students" icon={<PeopleIcon />} {...a11yProps(1)} />
            <Tab 
              label={
                <Badge badgeContent={lessonRequests.length} color="error" variant="standard">
                  <Box component="span" sx={{ px: 1 }}>Lesson Requests</Box>
                </Badge>
              } 
              icon={<RequestsIcon />} 
              {...a11yProps(2)} 
            />
          </Tabs>
        </Box>
        
        <TabPanel value={value} index={0}>
          <Box sx={{ height: '70vh' }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              selectable
              view={calendarView}
              onView={(v) => setCalendarView(v)}
              date={calendarDate}
              onNavigate={(d) => setCalendarDate(d)}
              views={['month', 'week', 'day']}
              step={30}
              timeslots={2}
              eventPropGetter={eventStyleGetter}
              culture="en-US"
            />
          </Box>
        </TabPanel>
        
        <TabPanel value={value} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} mt={1} flexWrap="wrap" gap={2}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#37474f' }}>
              My Students and Progress Status
            </Typography>
            <Box display="flex" gap={1.5}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CampaignIcon />}
                onClick={() => handleOpenMessageDialog()}
                sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
              >
                Send Group Announcement
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => navigate('/instructor/students')}
                sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
              >
                Student Registration & Management
              </Button>
            </Box>
          </Box>
          
          {students.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#fcfcfc', borderRadius: 3, border: '1px solid #e0e0e0' }}>
              <PeopleIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#37474f' }}>
                No Students Found
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                You do not have any registered or assigned students yet.
              </Typography>
            </Paper>
          ) : (
            <Box display="flex" flexDirection="column" gap={2}>
              <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid #e0e0e0' }}>
                <Table>
                  <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Student Name</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Student Number</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Transmission Type</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Completed Lessons</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Syllabus Progress (DVSA)</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((student) => (
                      <TableRow key={student.studentId} hover>
                        <TableCell sx={{ fontWeight: 'semibold' }}>{student.studentName}</TableCell>
                        <TableCell>{student.studentNumber || '-'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={student.transmissionType === 'Automatic' ? 'Automatic' : 'Manual'} 
                            color={student.transmissionType === 'Automatic' ? 'secondary' : 'default'}
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {student.completedLessons} / {student.totalLessons}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: '30%' }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Box sx={{ width: '100%' }}>
                              <LinearProgress 
                                variant="determinate" 
                                value={((student.coveredDvsaTopicsCount || 0) / 19) * 100} 
                                sx={{ 
                                  height: 6, 
                                  borderRadius: 3, 
                                  backgroundColor: '#eceff1',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: '#4caf50'
                                  }
                                }}
                              />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 45 }}>
                              {student.coveredDvsaTopicsCount || 0}/19
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Button
                              variant="outlined"
                              color="secondary"
                              size="small"
                              startIcon={<MessageIcon />}
                              onClick={() => handleOpenMessageDialog(student.studentId)}
                              sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                              Message
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              onClick={() => navigate('/instructor/students')}
                              sx={{ borderRadius: 1.5, textTransform: 'none' }}
                            >
                              Details & Manage
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </TabPanel>

        <TabPanel value={value} index={2}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2, color: '#37474f' }}>
            Pending Lesson Requests
          </Typography>
          
          {lessonRequests.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3, backgroundColor: '#fcfcfc' }}>
              <Typography variant="body1" color="textSecondary">
                There are currently no lesson requests pending approval.
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date / Time</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lessonRequests.map((req) => {
                    const date = new Date(req.lessonDate);
                    return (
                      <TableRow key={req.id} hover>
                        <TableCell sx={{ fontWeight: 'medium' }}>{req.studentName}</TableCell>
                        <TableCell>{format(date, 'dd.MM.yyyy - HH:mm')}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${req.duration} Minutes`} 
                            size="small" 
                            variant="outlined" 
                            color="primary" 
                            sx={{ fontWeight: 'bold' }} 
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 250, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                          {req.notes || <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>No notes</Typography>}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center' }}>
                          <Box display="flex" justifyContent="center" gap={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              onClick={() => handleApproveRequest(req.id)}
                              disabled={actionInProgress !== null}
                              sx={{ borderRadius: 2, fontWeight: 'bold' }}
                            >
                              Approve
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="small"
                              startIcon={<CancelIcon />}
                              onClick={() => handleRejectRequest(req.id)}
                              disabled={actionInProgress !== null}
                              sx={{ borderRadius: 2, fontWeight: 'bold' }}
                            >
                              Reject
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>
      </Box>
      {/* Send Message & Announcement Dialog */}
      <Dialog
        open={isMsgDialogOpen}
        onClose={() => setIsMsgDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleSendMessage}>
          <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e', display: 'flex', alignItems: 'center', gap: 1 }}>
            <MessageIcon color="primary" /> {msgData.studentId ? 'Send Message to Student' : 'Send Announcement to All Students'}
          </DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="msg-student-label">Recipient</InputLabel>
                <Select
                  labelId="msg-student-label"
                  value={msgData.studentId}
                  onChange={(e) => setMsgData(prev => ({ ...prev, studentId: e.target.value }))}
                  label="Recipient"
                  disabled={!!msgData.studentId}
                >
                  <MenuItem value=""><em>All Students (Group Announcement)</em></MenuItem>
                  {students.map((s) => (
                    <MenuItem key={s.studentId} value={s.studentId}>{s.studentName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Message Content"
                required
                fullWidth
                multiline
                rows={4}
                placeholder="Write the message you want to send to your student or class..."
                value={msgData.message}
                onChange={(e) => setMsgData(prev => ({ ...prev, message: e.target.value }))}
              />

              <Box display="flex" flexDirection="column" gap={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={msgData.sendEmail}
                      onChange={(e) => setMsgData(prev => ({ ...prev, sendEmail: e.target.checked }))}
                    />
                  }
                  label="Also send as email (Simulated)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={msgData.sendSms}
                      onChange={(e) => setMsgData(prev => ({ ...prev, sendSms: e.target.checked }))}
                    />
                  }
                  label="Also send as SMS (Simulated)"
                />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={() => setIsMsgDialogOpen(false)} color="inherit" disabled={isSendingMsg}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={isSendingMsg}>
              {isSendingMsg ? <CircularProgress size={24} color="inherit" /> : 'Send'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
};

export default InstructorDashboard;
